import React, { useEffect, useState } from 'react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import CompaniesPreviewModal from './CompaniesPreviewModal';
import useApiClient from '../../hooks/api/useApiClient';
import {
  calculateTotals,
  countAndPercentageLabelFormatter,
  generateMonthRange,
  getCompanyCountsMap,
  getCompanyIdsMap,
  getPeriodsMap,
} from './charts/chartHelpers';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const { RangePicker } = DatePicker;

const startDate = dayjs().subtract(5, 'months').startOf('month');
const endDate = dayjs().endOf('month');

export default function LiveAndInactiveTab({ selectedManagers, renderManagerSelect }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:CustomerBase:live_and_inactive_tab' });

  const { CustomerBaseStatsManager } = useApiClient();

  const [, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [totals, setTotals] = useState(null);
  const [monthRange, setMonthRange] = useState([startDate, endDate]);
  const [companiesModalOpen, setCompaniesModalOpen] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  useEffect(() => {
    fetchData();
  }, [monthRange, selectedManagers]);

  const fetchData = async () => {
    setLoading(true);
    const { data, success } = await CustomerBaseStatsManager.getLiveAndInactiveStats({
      start_date: monthRange[0].format('YYYY-MM-DD'),
      end_date: monthRange[1].format('YYYY-MM-DD'),
      managers: selectedManagers,
      client_timezone: moment.tz.guess(),
    });
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    setTotals(calculateTotals(data));
    setData(data);
  };

  const onBarSelect = (data) => {
    setSelectedCompanies(data.companyIds);
    setSelectedPeriod(data.period);
    setCompaniesModalOpen(true);
  };

  const chartData = {
    labels: data
      ? Object.keys(data.live).map((date) => moment(date, 'YYYY-MM').format('MMM YYYY'))
      : generateMonthRange(monthRange),
    datasets: [
      {
        label: t('dataset_labels.live'),
        data: getCompanyCountsMap(data, 'live'),
        backgroundColor: 'rgba(75, 192, 192, 0.7)',
        meta: {
          companyIds: getCompanyIdsMap(data, 'live'),
          periods: getPeriodsMap(data, 'live'),
        },
      },
      {
        label: t('dataset_labels.no_orders'),
        data: getCompanyCountsMap(data, 'no_orders'),
        backgroundColor: 'rgba(255, 206, 86, 0.7)',
        meta: {
          companyIds: getCompanyIdsMap(data, 'no_orders'),
          periods: getPeriodsMap(data, 'no_orders'),
        },
      },
      {
        label: t('dataset_labels.no_conversion'),
        data: getCompanyCountsMap(data, 'no_conversion'),
        backgroundColor: 'rgba(255, 182, 193, 0.7)',
        meta: {
          companyIds: getCompanyIdsMap(data, 'no_conversion'),
          periods: getPeriodsMap(data, 'no_conversion'),
        },
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      datalabels: {
        formatter: (value, context) => countAndPercentageLabelFormatter(value, totals[context.dataIndex]),
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
    onClick: (event, elements) => {
      if (elements.length < 1) {
        return;
      }

      const { datasetIndex } = elements[0];
      const barIndex = elements[0].index;

      onBarSelect({
        companyIds: chartData.datasets[datasetIndex].meta.companyIds[barIndex],
        period: chartData.datasets[datasetIndex].meta.periods[barIndex],
      });
    },
  };

  return (
    <>
      <RangePicker
        picker="month"
        value={monthRange}
        allowClear={false}
        onChange={(dates) => setMonthRange(dates)}
        disabledDate={(current) => current && current > dayjs().endOf('month')}
      />
      {renderManagerSelect()}
      <Bar data={chartData} options={chartOptions} />
      <CompaniesPreviewModal
        companyIds={selectedCompanies}
        period={selectedPeriod}
        open={companiesModalOpen}
        onClose={() => setCompaniesModalOpen(false)}
      />
    </>
  );
}
